import React, { useState, useCallback, useContext } from 'react';

import { AppContext } from '@prisjakt/ui-app-context';

import * as S from './styles';
import { IconCheckFilled, IconInfoFilled } from '@prisjakt/ui/icon';

export enum TextTypes {
  TextArea = 'textarea',
  Email = 'email',
  Text = 'text',
}

type Props = {
  name: string;
  required?: boolean;
  placeholder?: string;
  minLength?: number;
  defaultValue?: string | null;
  type: TextTypes;
};

const Text = ({
  name,
  required,
  placeholder,
  minLength = 0,
  defaultValue,
  type = TextTypes.TextArea,
}: Props) => {
  const { t } = useContext(AppContext);
  const [characterCount, setCharacterCount] = useState(
    defaultValue?.length || 0
  );
  const [showError, setShowError] = useState(false);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setCharacterCount(e.target.value.length);
    },
    []
  );

  const handleInvalidity = useCallback(
    (
      e:
        | React.InvalidEvent<HTMLInputElement>
        | React.InvalidEvent<HTMLTextAreaElement>
    ) => {
      setShowError(e.target.name === name);
    },
    [name]
  );

  return (
    <>
      <S.TextWrapper>
        {type === TextTypes.TextArea ? (
          <S.TextArea
            name={name}
            minLength={minLength}
            required={required}
            placeholder={placeholder}
            onChange={handleChange}
            onInvalid={handleInvalidity}
            warning={showError}
            defaultValue={defaultValue ?? undefined}
          />
        ) : (
          <S.TextInput
            name={name}
            type={type}
            placeholder={placeholder}
            required={required}
            onChange={handleChange}
            onInvalid={handleInvalidity}
            warning={showError}
            defaultValue={defaultValue ?? undefined}
          />
        )}

        <S.Counter>
          {(required && minLength > 0) || (characterCount && minLength > 0) ? (
            <S.Count
              color={characterCount >= minLength ? 'positive' : 'quaternary'}
            >
              {characterCount >= minLength ? (
                <IconCheckFilled size={16} color="positive500" />
              ) : null}
              {t('reviews', 'form.text-length-requirement', {
                curr: characterCount,
                min: minLength,
              })}
            </S.Count>
          ) : null}
        </S.Counter>
      </S.TextWrapper>

      {showError && (
        <S.Error>
          <IconInfoFilled size={16} color="negative700" />{' '}
          {type === TextTypes.Email
            ? t('errors', 'validation.email-invalid')
            : t('reviews', 'form.error-text')}
        </S.Error>
      )}
    </>
  );
};

export default Text;
